'use client';

import Image from 'next/image';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function GozAltiDolgusununOnerilmedigiDurumlar() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/goz-alti-dolgusunun-onerilmedigi-durumlar-kimler-icin-uygun-degil';
  const pageTitle = 'Göz Altı Dolgusunun Önerilmediği Durumlar: Kimler İçin Uygun Değil?';
  const pageDescription = 'Göz altı dolgusu her hastaya uygun mudur? Hangi durumlarda dolgu yapılmamalı veya ertelenmelidir? Uzman görüşüyle güvenli hasta seçimi rehberi.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Göz Altı Dolgusunun Önerilmediği Durumlar', href: '/tr/blog/goz-alti-dolgusunun-onerilmedigi-durumlar-kimler-icin-uygun-degil' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Göz altı dolgusu önerilmeyen durumlar ve hasta seçimi"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-amber-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Hasta Seçimi
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Göz Altı Dolgusunun Önerilmediği Durumlar: Kimler İçin Uygun Değil?
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>15 Mart 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>17 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-amber-600 text-white rounded-full hover:bg-amber-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-amber-400 text-white rounded-full hover:bg-amber-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-amber-700 text-white rounded-full hover:bg-amber-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Göz altı dolgusu, doğru kişiye doğru teknikle uygulandığında mucizevi bir gençleşme etkisi yaratır.
                Ancak herkes için uygun değildir.
                Bazı hastalarda dolgu etkisiz kalabilir, bazı durumlarda ise riskli hale gelir.
              </p>

              <div className="bg-amber-50 border-l-4 border-amber-400 p-6 mb-6">
                <p className="text-amber-800 font-medium text-lg">
                  "En iyi uygulama, doğru kişiye yapılmış olandır."
                </p>
                <p className="text-amber-800 font-medium mt-2">
                  — Dr. Barış Çin
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣ Her Dolgu Herkese Uygun Değildir
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Göz altı bölgesi yüzün en ince ve damar açısından en zengin bölgesidir.
                Bu nedenle dolgu yapılacak kişinin; cilt yapısı, morluk nedeni, yaş ve dolaşım durumu dikkatle analiz edilmelidir.
              </p>

              <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6">
                <p className="text-orange-800 font-medium">
                  📍 Dolgu, yorgunluğu gizler ama anatominin yerine geçemez.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2️⃣ Göz Altı Dolgusunun Önerilmediği Durumlar
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-amber-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Durum</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Neden</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Cilt çok ince ve elastikiyet kaybı yüksekse</td>
                      <td className="border border-gray-300 px-4 py-3">Dolgu yüzeyde kalır, Tyndall etkisi (mavi renk) oluşur</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Göz altı torbaları (yağ fıtıkları) belirginsa</td>
                      <td className="border border-gray-300 px-4 py-3">Dolgu torbayı daha da öne çıkarır</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Lenfatik dolaşım zayıfsa</td>
                      <td className="border border-gray-300 px-4 py-3">Kalıcı ödem gelişebilir</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Tiroid veya böbrek hastalığı varsa</td>
                      <td className="border border-gray-300 px-4 py-3">Su tutulum eğilimi artar</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Kronik alerji / sinüzit mevcutsa</td>
                      <td className="border border-gray-300 px-4 py-3">Göz çevresi sürekli şiş olur, dolgu etkisi bozulur</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Hamilelik veya emzirme dönemindeyse</td>
                      <td className="border border-gray-300 px-4 py-3">Tıbbi olarak önerilmez</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Daha önce dolgu sonrası komplikasyon geçirdiyse</td>
                      <td className="border border-gray-300 px-4 py-3">Tekrarlama riski vardır</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Psikolojik olarak sonuç beklentisi gerçekçi değilse</td>
                      <td className="border border-gray-300 px-4 py-3">Estetik tatminsizlik riski yüksek</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3️⃣ Göz Altı Torbası Olanlarda Neden Uygun Değil?
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Dolgu, hacim ekleyerek denge kurar.
                Ancak göz altında yağ fıtığı (torbalanma) varsa, dolgu bu şişliği daha da belirginleştirir.
                Bu hastalar için ideal çözüm, cerrahi alt göz kapağı ameliyatı (blefaroplasti) veya kombine tedavilerdir.
              </p>

              <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
                <p className="text-red-800 font-medium">
                  "Dolgu torbayı gizlemez; sadece şişliğin şeklini değiştirir."
                </p>
                <p className="text-red-800 font-medium mt-2">
                  — Dr. Barış Çin
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4️⃣ Dolaşım ve Lenf Sorunları
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Göz çevresinde sıvı birikimi eğilimi olan kişilerde dolgu sonrası ödem uzun süre kalabilir.
                Bu durum genellikle tiroid veya böbrek fonksiyon bozukluğu olan kişilerde görülür.
              </p>

              <div className="bg-yellow-50 rounded-xl p-6 mb-6">
                <h4 className="font-semibold text-yellow-900 mb-4">Tedbir:</h4>
                <ul className="list-disc list-inside space-y-2 text-yellow-800">
                  <li>Öncesinde hastanın genel durumu değerlendirilmelidir.</li>
                  <li>Dolgu yapılacaksa düşük viskoziteli ve su tutmayan ürün seçilmelidir.</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5️⃣ Göz Altı Morluğu Olanlarda Durum
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Göz altı morluğu iki nedenle olur:
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-blue-800">
                  <li>Damar rengi ve deri inceliği</li>
                  <li>Pigment artışı (melanin fazlalığı)</li>
                </ul>
                <p className="text-blue-800 mt-4">
                  Dolgu yalnızca hacim kaynaklı gölgelenmeyi düzeltir.
                  Eğer neden pigment veya damar ise, dolgu işe yaramaz.
                  Bu hastalarda lazer, PRP veya mezoterapi daha uygundur.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6️⃣ Alerjik ve Sinüzitli Kişiler
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Sürekli burun tıkanıklığı veya sinüzit yaşayan kişilerde göz çevresi dokusu hassastır.
                Dolgu sonrası lenfatik akım bozulabilir ve kalıcı şişlik gelişebilir.
              </p>

              <p className="text-gray-700 mb-4 leading-relaxed">
                Bu nedenle bu tür hastalarda dolgu ancak alerji kontrol altına alındıktan sonra yapılmalıdır.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                7️⃣ Hamilelik ve Emzirme Dönemi
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Tıbbi olarak hamilelikte veya emzirme döneminde dolgu yapılması önerilmez.
                Hyaluronik asit zararsız olsa da, enjeksiyon sırasında oluşabilecek enfeksiyon veya stres riskine karşı işlem ertelenmelidir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                8️⃣ Aşırı Beklentisi Olan Hastalar
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Bazı kişiler göz altı dolgusundan "ışık dolgusu mucizesi" bekler.
                Oysa dolgu yalnızca hacim düzeltir, cilt kalitesini değiştirmez.
                Bu nedenle psikolojik olarak gerçekçi beklentisi olmayan kişiler için işlem uygun değildir.
              </p>

              <div className="bg-purple-50 border-l-4 border-purple-400 p-4 mb-6">
                <p className="text-purple-800 font-medium">
                  "Dolgu mucize değil, doğru planlama sanatıdır."
                </p>
                <p className="text-purple-800 font-medium mt-2">
                  — Dr. Barış Çin
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                9️⃣ Alternatif Tedaviler
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-green-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Durum</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Uygun Alternatif</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Cilt inceliği</td>
                      <td className="border border-gray-300 px-4 py-3">PRP, mezoterapi, radyofrekans</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Yağ torbaları</td>
                      <td className="border border-gray-300 px-4 py-3">Alt göz kapağı cerrahisi</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Morluk</td>
                      <td className="border border-gray-300 px-4 py-3">Lazer veya kimyasal peeling</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Lenfatik ödem</td>
                      <td className="border border-gray-300 px-4 py-3">Lenf drenaj masajı ve diyet düzeni</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                🔟 Sık Sorulan Sorular (FAQ)
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">1. Göz altı torbam var, dolgu yaptırabilir miyim?</h3>
                  <p className="text-gray-600">Hayır, torba belirginse dolgu şişliği artırır.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2. Morluk varsa dolgu işe yarar mı?</h3>
                  <p className="text-gray-600">Sadece gölgelik kaynaklıysa evet, pigment kaynaklıysa hayır.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">3. Alerjim var, ne yapmalıyım?</h3>
                  <p className="text-gray-600">Önce alerji tedavi edilmelidir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">4. Dolgu yerine başka ne yapılabilir?</h3>
                  <p className="text-gray-600">PRP, lazer veya alt kapak ameliyatı uygun olabilir.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">5. Hamilelikte dolgu yapılabilir mi?</h3>
                  <p className="text-gray-600">Hayır, tıbbi olarak önerilmez.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">6. Tiroid hastasıyım, dolgu yaptırabilir miyim?</h3>
                  <p className="text-gray-600">Hastalık kontrol altındaysa ve doktor onayı varsa yapılabilir.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣1️⃣ Uzman Görüşü
              </h2>
              
              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-6">
                <p className="text-blue-800 italic">
                  "Göz altı dolgusu çok seçici bir işlemdir.
                  Her hastaya değil, doğru yapıya sahip hastaya yapılırsa güzel sonuç verir."
                </p>
                <p className="text-blue-800 font-medium mt-4">
                  — Dr. Barış Çin, Estetik Cerrahi Uzmanı
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣2️⃣ Sonuç ve Klinik Öneri
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Göz altı dolgusu mucizevi sonuçlar verir ama her yüz için uygun değildir.
                Yanlış hasta seçimi, en iyi ürünle bile kötü sonuç doğurabilir.
              </p>

              <div className="bg-red-50 border-l-4 border-red-400 p-6 mb-6">
                <p className="text-red-800 font-medium">
                  🩺 Dr. Barış Çin öneriyor:
                </p>
                <p className="text-red-800 mt-2">
                  "Her dolgu yapılmaz.
                  Doğru hastayı seçmek, doğru enjeksiyon yapmaktan daha önemlidir."
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>ISAPS (2024) – "Tear Trough Patient Selection Guide"</li>
                  <li>FDA (2023) – "Contraindications for Dermal Fillers"</li>
                  <li>TETD (2024) – "Göz Altı Dolgusu Hasta Seçimi Rehberi"</li>
                  <li>Harvard Health (2024) – "When Fillers Should Be Avoided"</li>
                </ul>
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#gözaltıdolgu</span>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#DrBarışÇin</span>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#hastaeğitimi</span>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#estetikbilinç</span>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#dolguuygunmu</span>
              </div>

              {/* Social Share Buttons */}
              <div className="border-t pt-8 mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Bu yazıyı paylaş:</h3>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaFacebookF className="w-4 h-4" />
                    <span>Facebook</span>
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="flex items-center space-x-2 bg-blue-400 text-white px-4 py-2 rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <FaTwitter className="w-4 h-4" />
                    <span>Twitter</span>
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="flex items-center space-x-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors"
                  >
                    <FaLinkedinIn className="w-4 h-4" />
                    <span>LinkedIn</span>
                  </button>
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <FaWhatsapp className="w-4 h-4" />
                    <span>WhatsApp</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-amber-600 to-orange-700 rounded-3xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Göz Altı Dolgusu Uygunluğu İçin Değerlendirme Alın</h3>
          <p className="mb-6">Uzman doktorumuzdan göz altı dolgusu uygunluğunuz hakkında detaylı değerlendirme alın.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`https://wa.me/905438999586?text=${encodeURIComponent('Göz altı dolgusu uygunluğum hakkında değerlendirme almak istiyorum.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
            >
              <FaWhatsapp className="w-5 h-5 mr-2" />
              WhatsApp ile İletişim
            </a>
            <a
              href="/tr/iletisim"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-amber-600 rounded-full hover:bg-gray-100 transition-colors"
            >
              Randevu Al
            </a>
          </div>
        </div>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <a
              href="/tr/blog/goz-alti-dolgusunun-tehlikeleri-bilimsel-gercekler-ve-guvenli-uygulama-kriterleri"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Göz Altı Dolgusunun Tehlikeleri</h4>
              <p className="text-gray-600 text-sm">Bilimsel gerçekler ve güvenli uygulama kriterleri.</p>
            </a>
            <a
              href="/tr/blog/goz-alti-isik-dolgusuna-uygunluk-kimler-icin-ideal-kimler-icin-degil"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Göz Altı Işık Dolgusuna Uygunluk</h4>
              <p className="text-gray-600 text-sm">Kimler için ideal, kimler için değil?</p>
            </a>
            <a
              href="/tr/blog/isik-dolgusunun-goz-alti-morluklarina-etkisi"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Işık Dolgusunun Göz Altı Morluklarına Etkisi</h4>
              <p className="text-gray-600 text-sm">Göz altı morluklarına etkisi ve sonuçları.</p>
            </a>
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <p className="text-gray-600 mb-4">
                Daha fazla bilgilendirici içerik için bizi takip edin.
              </p>
              <a
                href="/tr/blog"
                className="inline-flex items-center px-4 py-2 bg-amber-600 text-white rounded-full hover:bg-amber-700 transition-colors text-sm"
              >
                Tüm Blog Yazılarını Görüntüle
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
